<svelte:options customElement="hb-map" />

<script lang="ts">
  import { Map, MapBrowserEvent, View } from "ol";
  import { createEmpty, extend } from "ol/extent";
  import OSM from "ol/source/OSM";
  import XYZ from "ol/source/XYZ";
  import Feature from "ol/Feature";
  import Point from "ol/geom/Point";
  import {
    Circle as CircleStyle,
    Fill,
    Icon,
    Stroke,
    Style,
    Text,
  } from "ol/style";
  import {
    Tile as TileLayer,
    Vector as VectorLayer,
    VectorTile as VectorTileLayer,
  } from "ol/layer";
  import VectorSource from "ol/source/Vector";
  import VectorTileSource from "ol/source/VectorTile";
  import MVT from "ol/format/MVT";
  import { fromLonLat, transform } from "ol/proj";
  import type { Component } from "./types/webcomponent.type";

  import css from "./libs/ol-css";
  import debounce from "debounce";
  import Overlay from "ol/Overlay";
  import { onMount } from "svelte";

  /** Same filter as OSM `prerender`; reapplied when merging screenshot so export matches on-screen dark tiles. */
  const OSM_DARK_TILE_FILTER = "grayscale(80%) invert(100%)";
  const OSM_RASTER_LAYER_CLASS = "hb-map-osm-raster";

  /** Layer canvases that show OSM raster tiles (for screenshot merge; DOM class alone is not enough across OL versions). */
  function osmTileLayerCanvases(map: Map): HTMLCanvasElement[] {
    const out: HTMLCanvasElement[] = [];
    for (const layer of map.getLayers().getArray()) {
      if (!(layer instanceof TileLayer)) continue;
      const src = layer.getSource();
      if (!(src instanceof OSM)) continue;
      const r = layer.getRenderer() as
        | { getImage?: () => HTMLCanvasElement | OffscreenCanvas | null }
        | null;
      const img = r?.getImage?.();
      if (img instanceof HTMLCanvasElement) out.push(img);
    }
    return out;
  }

  function dispatch(name: string, detail: any) {
    $host().dispatchEvent(new CustomEvent(name, { detail }));
  }

  /** Accepts boolean or legacy string forms from JSON / HTML (`yes` / `no`, `true` / `false`). */
  function coerceSourceBoolean(value: unknown): boolean {
    if (value === true || value === 1) return true;
    if (value === false || value === 0 || value == null) return false;
    if (typeof value === "string") {
      const s = value.trim().toLowerCase();
      return s === "yes" || s === "true" || s === "1";
    }
    return false;
  }
  let map: Map | null = $state(null);
  /**
   * OSM raster tiles use invert/grayscale when the UI is dark: explicit `data-theme` /
   * `.theme-*` on `html` or `body`, then `prefers-color-scheme`.
   */
  let colorSchemeDark = $state(false);
  let mountEl: HTMLElement | null = $state(null);
  let popupEl: HTMLElement | undefined = $state(undefined);
  let popupContentEl: HTMLElement | null = $state(null);
  let overlay: Overlay | null;

  let {
    id = "",
    // style = "",
    center = [37.5176038, 15.0819224],
    zoom = 6,
    source = { type: "osm" },
    options = {},
    data = [],
    screenshot = "",
  }: Component = $props();

  $effect(() => {
    let changed = false;
    if (typeof source === "string") {
      source = JSON.parse(source);
      changed = true;
    }
    if (options && typeof options === "string") {
      options = JSON.parse(options);
      changed = true;
    }
    if (typeof zoom === "string") {
      zoom = Number(zoom);
      changed = true;
    }
    if (typeof center === "string") {
      center = JSON.parse(center);
      changed = true;
    }
    if (typeof data === "string") {
      data = JSON.parse(data);
      changed = true;
    }
    if (changed) {
      updateMap();
    }
  });

  $effect(() => {
    if (screenshot === "yes" && map) {
      takeScreenshot();
    }
  });

  function takeScreenshot() {
    if (!map || !mountEl) return;
    map.once("rendercomplete", function () {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          captureMapToPng();
        });
      });
    });
    map.renderSync();
  }

  function captureMapToPng() {
    if (!map || !mountEl) return;
    const size = map!.getSize();
    if (!size) return;
    const mapCanvas = document.createElement("canvas");
    mapCanvas.width = size[0];
    mapCanvas.height = size[1];
    const mapContext = mapCanvas.getContext("2d");
    if (!mapContext) return;
    const canvases =
      mountEl!.querySelectorAll<HTMLCanvasElement>(".ol-layer canvas");
    const srcObj =
      typeof source === "object" && source != null ? source : null;
    const isOsmSource = srcObj && String(srcObj.type).toLowerCase() === "osm";
    const osmRasterCanvasList =
      colorSchemeDark && isOsmSource ? osmTileLayerCanvases(map!) : null;

    canvases.forEach(function (canvas) {
      if (canvas.width > 0) {
        mapContext.save();
        const parentEl = canvas.parentNode as HTMLElement | null;
        const opacity = parentEl?.style?.opacity;
        mapContext.globalAlpha =
          !opacity || opacity === "" ? 1 : Number(opacity);
        const transform = canvas.style.transform;
        const match = transform.match(/^matrix\(([^)]*)\)$/);
        if (match) {
          const matrix = match[1].split(",").map(Number);
          mapContext.setTransform(
            matrix[0],
            matrix[1],
            matrix[2],
            matrix[3],
            matrix[4],
            matrix[5],
          );
        }
        const useOsmDarkRasterFilter = osmRasterCanvasList
          ? osmRasterCanvasList.includes(canvas) ||
            (osmRasterCanvasList.length === 0 &&
              !!canvas.closest(`.${OSM_RASTER_LAYER_CLASS}`))
          : false;

        if (useOsmDarkRasterFilter) {
          const tmp = document.createElement("canvas");
          tmp.width = canvas.width;
          tmp.height = canvas.height;
          const tctx = tmp.getContext("2d", { willReadFrequently: true });
          if (tctx) {
            tctx.filter = OSM_DARK_TILE_FILTER;
            tctx.drawImage(canvas, 0, 0);
            tctx.filter = "none";
            mapContext.drawImage(tmp, 0, 0);
          } else {
            mapContext.drawImage(canvas, 0, 0);
          }
        } else {
          mapContext.drawImage(canvas, 0, 0);
        }
        mapContext.restore();
      }
    });
    mapContext.globalAlpha = 1;
    mapContext.setTransform(1, 0, 0, 1, 0, 0);
    const base64 = mapCanvas.toDataURL("image/png");
    dispatch("screenshotTaken", { base64 });
  }

  function pointClick(
    e: MapBrowserEvent<KeyboardEvent | WheelEvent | PointerEvent>,
  ) {
    const coordinates = transform(e.coordinate, "EPSG:3857", "EPSG:4326");
    const c = map?.getView?.()?.getCenter?.();
    const currentCenter = transform(c || center, "EPSG:3857", "EPSG:4326");
    const currentZoom = map?.getView?.()?.getZoom?.() || zoom;

    const feature = map?.forEachFeatureAtPixel(e.pixel, function (feat, layer) {
      return feat;
    });

    if (feature && feature.get("marker")) {
      dispatch("markerClick", {
        coordinates: { latitude: coordinates[1], longitude: coordinates[0] },
        id: feature.get("id"),
      });
      if (feature.get("popupHtml") && popupContentEl?.innerHTML) {
        popupContentEl.innerHTML = feature.get("popupHtml");
        overlay?.setPosition(e.coordinate);
      }
    } else {
      overlay?.setPosition(undefined);

      dispatch("pointClickCoordinates", {
        coordinates: { latitude: coordinates[1], longitude: coordinates[0] },
        zoom: currentZoom,
        center: currentCenter,
      });
    }
  }

  const cartoThemes: Record<string, Record<string, string>> = {
    positron: {
      water: "#aad3df",
      waterway: "#aad3df",
      park: "#d8e8c8",
      landcover: "#e8e4de",
      landuse: "#ededed",
      building: "#d9d0c9",
      buildingStroke: "#cfcdca",
      road: "#ffffff",
      roadCasing: "#cfcdca",
      motorway: "#f0c070",
      primary: "#fbe29a",
      rail: "#bfbfbf",
      boundary: "#b3b3b3",
      placeLabel: "#4a4a4a",
      placeLabelHalo: "#ffffff",
    },
    dark_matter: {
      water: "#0e1626",
      waterway: "#0e1626",
      park: "#111c11",
      landcover: "#121212",
      landuse: "#181818",
      building: "#222222",
      buildingStroke: "#2a2a2a",
      road: "#3c3c3c",
      roadCasing: "#222222",
      motorway: "#4a4a4a",
      primary: "#444444",
      rail: "#333333",
      boundary: "#4a4a4a",
      placeLabel: "#d8d8d8",
      placeLabelHalo: "#1a1a1a",
    },
    voyager: {
      water: "#aad3df",
      waterway: "#aad3df",
      park: "#c8facc",
      landcover: "#e8e4de",
      landuse: "#ededed",
      building: "#e0d8d0",
      buildingStroke: "#d5cec4",
      road: "#ffffff",
      roadCasing: "#e0dcd8",
      motorway: "#f0c56c",
      primary: "#fbe29a",
      rail: "#bfbfbf",
      boundary: "#9e9cab",
      placeLabel: "#3d3d4a",
      placeLabelHalo: "#ffffff",
    },
  };

  type CartoStyleOpts = {
    boundaries_only: boolean;
    /** Resolved filter; empty Set means hide every boundary. */
    boundary_admin_levels: Set<number> | null;
    national_boundaries_only: boolean;
  };

  function parseBoundaryAdminLevels(
    raw: string | undefined,
  ): Set<number> | null {
    if (raw == null || typeof raw !== "string") return null;
    const s = raw.trim();
    if (!s) return null;
    try {
      const parsed = JSON.parse(s) as unknown;
      if (!Array.isArray(parsed) || parsed.length === 0) return null;
      const nums = new Set<number>();
      for (const item of parsed) {
        const n = Number(item);
        if (!Number.isNaN(n)) nums.add(n);
      }
      return nums.size > 0 ? nums : null;
    } catch {
      return null;
    }
  }

  /** Effective admin_level filter; `nationalOnly` restricts to level 2 intersected with `parsed`. */
  function resolveBoundaryAdminLevels(
    nationalOnly: boolean,
    parsed: Set<number> | null,
  ): Set<number> | null {
    if (!nationalOnly) return parsed;
    if (parsed) {
      const next = new Set<number>();
      for (const n of parsed) {
        if (n === 2) next.add(2);
      }
      return next.size > 0 ? next : new Set();
    }
    return new Set([2]);
  }

  function cartoStyleFn(preset: string, opts: CartoStyleOpts) {
    const t = cartoThemes[preset] || cartoThemes.voyager;
    const boundaryStrokeNational = new Style({
      stroke: new Stroke({
        color: t.boundary,
        width: 1.25,
      }),
    });
    const boundaryStrokeRegional = new Style({
      stroke: new Stroke({
        color: t.boundary,
        width: 1,
        lineDash: [4, 4],
      }),
    });

    function boundaryAllowed(feature: any): boolean {
      if (!opts.boundary_admin_levels) return true;
      if (opts.boundary_admin_levels.size === 0) return false;
      const al = feature.get("admin_level");
      const n = al === undefined || al === null ? NaN : Number(al);
      return opts.boundary_admin_levels.has(n);
    }

    function boundaryStrokeForFeature(feature: any): Style {
      if (opts.national_boundaries_only) return boundaryStrokeNational;
      const al = feature.get("admin_level");
      const n = al === undefined || al === null ? NaN : Number(al);
      return n === 2 ? boundaryStrokeNational : boundaryStrokeRegional;
    }

    /** Country names only (`class === "country"`) from the `place` layer — no cities or regions. */
    function placeCountryLabelStyle(feature: any): Style | null {
      if (feature.get("class") !== "country") return null;
      const name = feature.get("name") as string | undefined;
      if (!name) return null;
      return new Style({
        text: new Text({
          text: name,
          font: "600 13px sans-serif",
          fill: new Fill({ color: t.placeLabel }),
          stroke: new Stroke({
            color: t.placeLabelHalo,
            width: 4,
          }),
        }),
      });
    }

    return function (feature: any) {
      const layer = feature.get("layer");
      if (opts.boundaries_only) {
        if (layer === "place") {
          const labelStyle = placeCountryLabelStyle(feature);
          if (labelStyle) return labelStyle;
          return null;
        }
        if (layer !== "boundary") return null;
        if (!boundaryAllowed(feature)) return null;
        return boundaryStrokeForFeature(feature);
      }
      switch (layer) {
        case "water":
          return new Style({ fill: new Fill({ color: t.water }) });
        case "waterway":
          return new Style({
            stroke: new Stroke({ color: t.waterway, width: 1 }),
          });
        case "park":
        case "landcover":
          return new Style({ fill: new Fill({ color: t.landcover }) });
        case "landuse":
          return new Style({ fill: new Fill({ color: t.landuse }) });
        case "building": {
          return new Style({
            fill: new Fill({ color: t.building }),
            stroke: new Stroke({ color: t.buildingStroke, width: 0.5 }),
          });
        }
        case "transportation": {
          const cls = feature.get("class");
          let color = t.road;
          let width = 1;
          if (cls === "motorway" || cls === "trunk") {
            color = t.motorway;
            width = 3;
          } else if (cls === "primary" || cls === "secondary") {
            color = t.primary;
            width = 2;
          } else if (cls === "rail") {
            color = t.rail;
            width = 1;
          }
          return new Style({
            stroke: new Stroke({ color, width }),
          });
        }
        case "boundary":
          if (!boundaryAllowed(feature)) return null;
          return boundaryStrokeForFeature(feature);
        case "aeroway":
          return new Style({
            stroke: new Stroke({ color: t.road, width: 1 }),
            fill: new Fill({ color: t.landuse }),
          });
        case "place": {
          return placeCountryLabelStyle(feature);
        }
        case "poi":
        case "housenumber":
        case "transportation_name":
        case "water_name":
        case "aerodrome_label":
        case "mountain_peak":
          return null;
        default: {
          const fallbackColor =
            preset === "dark_matter"
              ? t.landuse
              : preset === "positron"
                ? t.landuse
                : t.landcover;
          return new Style({
            fill: new Fill({ color: fallbackColor }),
            stroke: new Stroke({ color: fallbackColor, width: 1 }),
          });
        }
      }
    };
  }

  function updateMap() {
    if (mountEl) {
      if (map) {
        map = null;
        const style = document.createElement("style");
        style.innerHTML = css;
        mountEl.innerHTML = "";
        mountEl.appendChild(style);
      }

      const layers = [];
      if (source.type.toLowerCase() === "osm") {
        const tile = new TileLayer({
          className: `ol-layer ${OSM_RASTER_LAYER_CLASS}`,
          source: new OSM(),
        });
        if (colorSchemeDark) {
          tile.on("prerender", (evt) => {
            if (evt.context) {
              const context = evt.context as CanvasRenderingContext2D;
              context.filter = `${OSM_DARK_TILE_FILTER} `;
              context.globalCompositeOperation = "source-over";
            }
          });
          tile.on("postrender", (evt) => {
            if (evt.context) {
              const context = evt.context as CanvasRenderingContext2D;
              context.filter = "none";
            }
          });
        }
        layers.push(tile);
      } else if (source.type.toLowerCase() === "xyz" && source.url) {
        layers.push(
          new TileLayer({
            source: new XYZ({ url: source.url }),
          }),
        );
      } else if (source.type.toLowerCase() === "carto_vector") {
        const requestedStyle = source.style;
        const preset = colorSchemeDark
          ? "dark_matter"
          : requestedStyle || "voyager";
        const theme = cartoThemes[preset] || cartoThemes.voyager;
        const boundaries_only = coerceSourceBoolean(source.boundaries_only);
        const national_boundaries_only = coerceSourceBoolean(
          source.national_boundaries_only,
        );
        const boundary_admin_levels = resolveBoundaryAdminLevels(
          national_boundaries_only,
          parseBoundaryAdminLevels(source.boundary_admin_levels),
        );
        layers.push(
          new VectorTileLayer({
            source: new VectorTileSource({
              format: new MVT(),
              url:
                source.url ||
                "https://tiles-d.basemaps.cartocdn.com/vectortiles/carto.streets/v1/{z}/{x}/{y}.mvt",
            }),
            style: cartoStyleFn(preset, {
              boundaries_only,
              boundary_admin_levels,
              national_boundaries_only,
            }) as any,
            background: preset === "dark_matter" ? theme.landuse : undefined,
          }),
        );
      }
      let markersCenter: number[][] = [];
      let extent = createEmpty();

      if (data?.filter?.((f) => f.marker)?.length) {
        const markersToAdd = [];
        for (const marker of data
          .filter((f) => f.marker)
          .map((m) => m.marker)) {
          // let markerCoords: number[];
          // if (marker.lngLat) {
          // 	markerCoords = marker.lngLat;
          // } else if (marker.nmea) {
          // 	try {
          // 		const nmeaParsed: GPSState = Parse(marker.nmea) as GPSState;
          // 		if (!nmeaParsed || !nmeaParsed.lat || !nmeaParsed.lon) throw new Error("invalid parse");
          // 		markerCoords = [nmeaParsed.lat, nmeaParsed.lon];
          // 	} catch (err) {
          // 		throw new Error("invalid nmea marker " + marker.nmea);
          // 	}
          // } else {
          // 	throw new Error("unsupported marker");
          // }
          const iconFeature = new Feature({
            geometry: marker?.lngLat
              ? new Point(fromLonLat(marker.lngLat))
              : undefined,
            icon: marker?.icon,
            marker: true,
            id: marker?.id || undefined,
            popupHtml: marker?.popupHtml,
            label: marker?.text,
            textPosition:
              marker?.text_position || options.text_position || "right",
            textOffset: marker?.text_offset ?? options.text_offset,
          });

          markersToAdd.push(iconFeature);
          const coords = iconFeature.getGeometry()?.getCoordinates();
          if (marker?.lngLat) markersCenter.push(marker.lngLat);
          if (
            options.centerFromGeometries &&
            coords &&
            coords[2] !== -Infinity &&
            coords[3] !== -Infinity
          ) {
            console.log("coords", coords);
            //add extent of every feature to the extent
            extent = extend(extent, coords);
          }
        }
        const defaultIcon = new Style({
          image: new Icon({
            scale: 3,
            anchor: [0.5, 0.8],

            src: "https://upload.wikimedia.org/wikipedia/commons/e/e7/Maki2-marker-18.svg",
          }),
        });
        const v = new VectorLayer({
          source: new VectorSource({
            features: markersToAdd,
          }),
          style: function (feature) {
            const icon = feature.get("icon");
            const label = feature.get("label");
            const textPosition = feature.get("textPosition") || "right";
            const textOffset = feature.get("textOffset");

            let textOpts: {
              offsetX: number;
              offsetY: number;
              textAlign: CanvasTextAlign;
              textBaseline: CanvasTextBaseline;
            } = {
              offsetX: 0,
              offsetY: 0,
              textAlign: "center",
              textBaseline: "middle",
            };

            if (icon?.uri && label) {
              const scale = icon.scale || 0.15;
              const anchorX = icon.anchor?.[0] ?? 0.5;
              const anchorY = icon.anchor?.[1] ?? 0.8;
              const estSize = scale >= 1 ? 18 * scale : 150 * scale;
              const centerOffsetY = -(anchorY - 0.5) * estSize;
              const centerOffsetX = -(anchorX - 0.5) * estSize;
              const gap = textOffset ?? 6;
              const halfSize = estSize / 2;

              switch (textPosition) {
                case "right":
                  textOpts.offsetX = centerOffsetX + halfSize + gap;
                  textOpts.offsetY = centerOffsetY + 3;
                  textOpts.textAlign = "left";
                  textOpts.textBaseline = "middle";
                  break;
                case "left":
                  textOpts.offsetX = centerOffsetX - halfSize - gap;
                  textOpts.offsetY = centerOffsetY + 3;
                  textOpts.textAlign = "right";
                  textOpts.textBaseline = "middle";
                  break;
                case "top":
                  textOpts.offsetX = centerOffsetX;
                  textOpts.offsetY = centerOffsetY - halfSize - gap;
                  textOpts.textAlign = "center";
                  textOpts.textBaseline = "bottom";
                  break;
                case "bottom":
                  textOpts.offsetX = centerOffsetX;
                  textOpts.offsetY = centerOffsetY + halfSize + gap;
                  textOpts.textAlign = "center";
                  textOpts.textBaseline = "top";
                  break;
              }
            }

            const fontSize = Math.round(13 * (options.text_scale ?? 1));
            const textStyle = label
              ? new Text({
                  text: label,
                  font: `${fontSize}px sans-serif`,
                  fill: new Fill({ color: "#333" }),
                  stroke: new Stroke({ color: "#fff", width: 3 }),
                  ...textOpts,
                })
              : undefined;
            if (icon?.uri) {
              return new Style({
                image: new Icon({
                  scale: icon.scale || 0.15,
                  anchor: icon.anchor || [0.5, 0.8],
                  src: icon.uri,
                  opacity: icon.opacity || 1,
                  color: icon.color || undefined,
                }),
                text: textStyle,
              });
            } else if (label) {
              return new Style({ text: textStyle });
            } else {
              return defaultIcon;
            }
          },
        });
        layers.push(v);
        // markersCenter=v.getBounds().getCenterLonLat()
      }
      overlay = new Overlay({
        element: popupEl,
        autoPan: {
          animation: {
            duration: 250,
          },
        },
      });
      map = new Map({
        target: mountEl,
        layers,
        overlays: [overlay],
        view: new View({
          center: fromLonLat(center),
          zoom,
        }),
      });
      if (
        options?.centerFromGeometries &&
        markersCenter &&
        markersCenter.length > 1 &&
        extent[2] !== -Infinity &&
        extent[3] !== -Infinity
      ) {
        console.log("extent", extent);
        map.getView().fit(extent, { padding: [25, 25, 25, 25] });
      } else if (options?.centerFromGeometries) {
        map.getView().setCenter(fromLonLat(markersCenter[0]));
      }
      map.on("singleclick", function (e) {
        pointClick(e);
      });
    }
  }

  function popupClose(e?: { target: any }) {
    overlay?.setPosition(undefined);
    e?.target?.blur?.();
  }

  /**
   * Matches Bulma-style hooks: `hb-map` host, then `html` / `body` (same pattern as other hb-*).
   */
  function documentExplicitColorScheme(): "dark" | "light" | null {
    for (const el of [
      $host() as Element | undefined,
      document.documentElement,
      document.body,
    ]) {
      if (!el) continue;
      const dt = el.getAttribute("data-theme");
      if (dt === "dark") return "dark";
      if (dt === "light") return "light";
      if (el.classList.contains("theme-dark")) return "dark";
      if (el.classList.contains("theme-light")) return "light";
    }
    return null;
  }

  function computeColorSchemeDark(): boolean {
    const explicit = documentExplicitColorScheme();
    if (explicit === "dark") return true;
    if (explicit === "light") return false;
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  }

  function onColorSchemeSignal() {
    const next = computeColorSchemeDark();
    if (next === colorSchemeDark) return;
    colorSchemeDark = next;
    updateMap();
  }

  onMount(() => {
    mountEl = $host()?.shadowRoot?.querySelector("#map") as HTMLElement;
    popupEl = $host()?.shadowRoot?.querySelector("#popup") as HTMLElement;
    popupContentEl = $host()?.shadowRoot?.querySelector(
      "#popup-content",
    ) as HTMLElement;

    const style = document.createElement("style");
    style.innerHTML = css;
    mountEl?.appendChild(style);

    colorSchemeDark = computeColorSchemeDark();

    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    mq.addEventListener("change", onColorSchemeSignal);

    const docRoots = [document.documentElement, document.body].filter(
      Boolean,
    ) as Element[];
    const docMo = new MutationObserver(onColorSchemeSignal);
    for (const el of docRoots) {
      docMo.observe(el, {
        attributes: true,
        attributeFilter: ["data-theme", "class"],
      });
    }

    const host = $host();
    let hostMo: MutationObserver | null = null;
    if (host) {
      hostMo = new MutationObserver(onColorSchemeSignal);
      hostMo.observe(host, {
        attributes: true,
        attributeFilter: ["data-theme", "class"],
      });
    }

    updateMap();

    return () => {
      mq.removeEventListener("change", onColorSchemeSignal);
      docMo.disconnect();
      hostMo?.disconnect();
    };
  });
</script>

<svelte:window on:resize={debounce(updateMap, 200)} />
<div id="map"></div>
<div id="popup" class="ol-popup">
  <button
    onclick={(e) => popupClose(e)}
    id="popup-closer"
    class="ol-popup-closer"
    aria-label="Close popup"
  ></button>
  <div id="popup-content"></div>
</div>

<!-- svelte-ignore css_unused_selector (:host `data-theme` / theme classes are set by consumers; shared Bulma host theme includes matching selectors) -->
<style lang="scss">
  @forward "./styles/bulma.scss";
  @forward "./styles/webcomponent.scss";
</style>
